import { useTranslationCommonForm } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import * as Yup from 'yup';

import { COUNTRIES_OPTIONS, COUNTRY_CODES } from '@dealstack/shared/lib/countries/countriesData';
import { INPUT_NAME_MAX_LENGTH, INPUT_PERSON_NAME_MAX_LENGTH } from '@dealstack/shared/lib/utils';
import { isPhoneValid } from '@dealstack/shared/lib/validation';

const ZIP_CODE_REQUIRED_LENGTH = 5;

const PASSWORD_REGEX =
	/^(?=.*\p{Lu})(?=.*\d)(?=.*[!@#$%^&*()_+{}[\]:;<>,.?~\\/-])[\p{L}\d!@#$%^&*()_+{}[\]:;<>,.?~\\/-]{8,}$/u;

const MIN_PASSWORD_LENGTH = 8;
const MAX_PASSWORD_LENGTH = 256;

const IBAN_MIN_LENGTH = 5;
export const IBAN_MAX_LENGTH = 35;

export const useCommonYupValidation = () => {
	const tCommonForm = useTranslationCommonForm();
	const password = Yup.string()
		.test({
			name: 'valid',
			message: '',
			test: (val = '') => {
				return !(
					val?.length < MIN_PASSWORD_LENGTH ||
					val?.length > MAX_PASSWORD_LENGTH ||
					!PASSWORD_REGEX.test(val)
				);
			},
		})
		.typeError(tCommonForm('invalid_type'))
		.nullable();
	const passwordLengthValidation = Yup.string()
		.max(
			MAX_PASSWORD_LENGTH,
			tCommonForm('common.max_length', {
				max_length: MAX_PASSWORD_LENGTH,
			})
		)
		.required(tCommonForm('field_required'));
	const stringFieldOptional = Yup.string()
		.trim(tCommonForm('field_trimming_required'))
		.typeError(tCommonForm('invalid_type'))
		.optional()
		.nullable();
	const stringFieldRequired = Yup.string()
		.trim(tCommonForm('field_trimming_required'))
		.required(tCommonForm('field_required'));
	const stringArrayFieldRequired = Yup.array()
		.of(Yup.string().required(tCommonForm('field_required')))
		.min(1, tCommonForm('field_required'))
		.required(tCommonForm('field_required'));
	const numberFieldRequired = Yup.number().required(tCommonForm('field_required'));
	const booleanFieldRequiredTrue = Yup.boolean().oneOf([true], 'Field must be checked');
	const optionalEmail = Yup.string()
		.trim(tCommonForm('field_trimming_required'))
		.email(tCommonForm('invalid_email'))
		.typeError(tCommonForm('invalid_type'));
	const requiredConsent = Yup.bool()
		.oneOf([true], tCommonForm('consent_required'))
		.typeError(tCommonForm('invalid_type'))
		.required(tCommonForm('consent_required'));
	const contractName = Yup.string().test({
		name: 'not allowed symbols',
		message: tCommonForm('file_name.invalid_format'),
		test: (val = '') => !/[.?*:/"<>|\\]/.test(val),
	});

	const zipCode = Yup.string()
		.test({
			name: 'len',
			message: tCommonForm('zip_code.too_short'),
			test: (val = '') => val.length >= ZIP_CODE_REQUIRED_LENGTH,
		})
		.test({
			name: 'len',
			message: tCommonForm('zip_code.too_long'),
			test: (val = '') => val.length <= ZIP_CODE_REQUIRED_LENGTH,
		})
		.typeError(tCommonForm('invalid_type'));

	const optionalPhone = Yup.string()
		.test({
			name: 'valid',
			message: tCommonForm('phone_number.invalid'),
			test: (val = '', yup) => {
				const { required } = yup.schema.exclusiveTests;
				if (!val || (!required && val.length === 0)) {
					return true;
				}
				const isValid = isPhoneValid(val);
				return isValid;
			},
		})
		.typeError(tCommonForm('invalid_type'));

	const dateInTheFutureOptional = Yup.date()
		.typeError(tCommonForm('invalid_date'))
		.min(new Date(), tCommonForm('date.in_the_future'));
	const dateInThePastOptional = Yup.date()
		.typeError(tCommonForm('invalid_date'))
		.max(new Date(), tCommonForm('date.in_the_past'));

	const iban = Yup.string()
		.test({
			name: 'minLength',
			message: tCommonForm('iban.short'),
			test: (val, yup) => {
				const { required } = yup.schema.exclusiveTests;
				const stringValue = val?.toString();
				if (!stringValue) {
					return true;
				}
				if (!required && stringValue?.length === 0) {
					return true;
				}
				return stringValue.length >= IBAN_MIN_LENGTH;
			},
		})
		.test({
			name: 'maxLength',
			message: tCommonForm('iban.long'),
			test: (val) => {
				const stringValue = val?.toString();
				if (!stringValue) {
					return true;
				}
				return val ? stringValue.length <= IBAN_MAX_LENGTH : false;
			},
		})
		.typeError(tCommonForm('invalid_type'));

	const country = Yup.string().oneOf(
		COUNTRIES_OPTIONS.map((option) => option.value),
		tCommonForm('invalid_country')
	);

	const countryCode = Yup.string().oneOf(COUNTRY_CODES, tCommonForm('invalid_country'));

	const inputNameString = Yup.string()
		.trim(tCommonForm('field_trimming_required'))
		.max(
			INPUT_NAME_MAX_LENGTH,
			tCommonForm('common.max_length', {
				max_length: INPUT_NAME_MAX_LENGTH,
			})
		);

	const inputPersonNameString = Yup.string()
		.trim(tCommonForm('field_trimming_required'))
		.max(
			INPUT_PERSON_NAME_MAX_LENGTH,
			tCommonForm('common.max_length', {
				max_length: INPUT_PERSON_NAME_MAX_LENGTH,
			})
		);

	return {
		requiredConsent,
		string: {
			required: stringFieldRequired,
			optional: stringFieldOptional,
		},
		stringArray: {
			required: stringArrayFieldRequired,
		},
		number: {
			required: numberFieldRequired,
		},
		boolean: {
			required: booleanFieldRequiredTrue,
		},
		password,
		passwordLengthValidation,
		email: {
			optional: optionalEmail,
			required: optionalEmail
				.trim(tCommonForm('field_trimming_required'))
				.required(tCommonForm('field_required')),
		},
		phone: {
			optional: optionalPhone,
			required: optionalPhone.required(tCommonForm('field_required')),
		},
		zip_code: {
			optional: zipCode,
			required: zipCode
				.trim(tCommonForm('field_trimming_required'))
				.required(tCommonForm('field_required')),
		},
		contract_name: {
			optional: contractName,
			required: contractName
				.trim(tCommonForm('field_trimming_required'))
				.required(tCommonForm('field_required')),
		},
		date: {
			future: {
				optional: dateInTheFutureOptional,
				required: dateInTheFutureOptional.required(tCommonForm('field_required')),
			},
			past: {
				optional: dateInThePastOptional,
				required: dateInThePastOptional.required(tCommonForm('field_required')),
			},
		},
		iban: {
			optional: iban,
			required: iban.required(tCommonForm('field_required')),
			changing: iban.when('usingIban', {
				is: true,
				then: (_schema) => iban.required(tCommonForm('field_required')),
			}),
		},
		country: {
			optional: country,
			required: country.required(tCommonForm('field_required')),
		},
		countryCode: {
			optional: countryCode,
			required: countryCode.required(tCommonForm('field_required')),
		},
		countryCodeArray: {
			required: Yup.array()
				.of(countryCode.required(tCommonForm('field_required')))
				.min(1, tCommonForm('field_required'))
				.required(tCommonForm('field_required')),
		},
		inputNameString: {
			optional: inputNameString,
			required: inputNameString.required(tCommonForm('field_required')),
		},
		inputPersonNameString: {
			optional: inputPersonNameString,
			required: inputPersonNameString.required(tCommonForm('field_required')),
		},
	};
};
